import { useState, useEffect } from 'react';
import api from '../utils/api';

const EMPTY = {
  name: '', contact_name: '', email: '', phone: '', address: '', tax_id: '', net_terms: 45, notes: '',
};

export default function ClientEditDrawer({ client, onClose, onSaved }) {
  const [form, setForm] = useState(() => client ? { ...EMPTY, ...client } : EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isEdit = Boolean(client?.id);

  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(isEdit);
  const [newContact, setNewContact] = useState({ contact_type: 'email', value: '', label: '' });

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/clients/${client.id}/contacts`)
      .then(r => setContacts(r.data))
      .finally(() => setLoadingContacts(false));
  }, [isEdit, client]);

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
  }

  async function addContact() {
    if (!newContact.value.trim()) return;
    try {
      const r = await api.post(`/clients/${client.id}/contacts`, newContact);
      const refreshed = await api.get(`/clients/${client.id}/contacts`); setContacts(refreshed.data);
      setNewContact({ contact_type: 'email', value: '', label: '' });
    } catch (e) {
      setError(e.response?.data?.error || 'Could not add contact');
    }
  }

  async function removeContact(contactId) {
    try {
      await api.delete(`/clients/${client.id}/contacts/${contactId}`);
      setContacts(c => c.filter(x => x.id !== contactId));
    } catch (e) {
      setError(e.response?.data?.error || 'Could not remove contact');
    }
  }

  async function makePrimary(contactId, contactType) {
    try {
      await api.put(`/clients/${client.id}/contacts/${contactId}`, { is_primary: true });
      setContacts(c => c.map(x => x.contact_type === contactType ? { ...x, is_primary: x.id === contactId ? 1 : 0 } : x));
    } catch (e) {
      setError(e.response?.data?.error || 'Could not update contact');
    }
  }

  async function handleSave() {
    if (!form.name) {
      setError('Client name is required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      let clientId = client?.id;
      if (isEdit) {
        await api.put(`/clients/${client.id}`, form);
      } else {
        const r = await api.post('/clients', form);
        clientId = r.data.id;
      }

      if (newContact.value.trim()) {
        await api.post(`/clients/${clientId}/contacts`, newContact);
      }

      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save client');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className="panel-overlay" onClick={onClose} />
      <div className="edit-drawer">
        <div className="drawer-header">
          <h3 className="drawer-title">{isEdit ? 'Edit Client' : 'Add Client'}</h3>
          <button className="drawer-close" onClick={onClose}>×</button>
        </div>

        <div className="drawer-body">
          {error && (
            <div style={{ background: 'var(--danger-bg)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius)', fontSize: 13, marginBottom: 18 }}>
              {error}
            </div>
          )}

          <div className="form-grid">
            <div className="form-field span-2">
              <label>Client name</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
            </div>

            <div className="form-field">
              <label>Contact name</label>
              <input value={form.contact_name || ''} onChange={e => set('contact_name', e.target.value)} />
            </div>
            <div className="form-field">
              <label>Net terms (days)</label>
              <input type="number" value={form.net_terms} onChange={e => set('net_terms', Number(e.target.value))} />
            </div>

            <div className="form-field">
              <label>Tax ID</label>
              <input value={form.tax_id || ''} onChange={e => set('tax_id', e.target.value)} />
            </div>
            <div></div>

            <div className="form-field span-2">
              <label>Address</label>
              <input value={form.address || ''} onChange={e => set('address', e.target.value)} />
            </div>
          </div>

          <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 4px' }}>Emails & phone numbers</h4>
            <p className="form-hint" style={{ marginBottom: 14 }}>
              Add more than one if this client has multiple contacts (e.g. AP team vs. account manager).
              {!isEdit && ' This first one will be saved automatically when you click "Add client" below.'}
            </p>

            {isEdit && (
              <>
                {loadingContacts && <div className="form-hint">Loading contacts…</div>}
                {!loadingContacts && contacts.length === 0 && (
                  <div className="form-hint">No additional contacts yet — the email/phone above will be used.</div>
                )}
                {!loadingContacts && contacts.map(c => (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                    <span className="badge badge-neutral" style={{ minWidth: 50, justifyContent: 'center' }}>
                      {c.contact_type === 'email' ? 'Email' : 'Phone'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14 }}>{c.value}</div>
                      {c.label && <div className="form-hint">{c.label}</div>}
                    </div>
                    {c.is_primary
                      ? <span className="badge badge-success">Primary</span>
                      : <button className="btn btn-ghost" onClick={() => makePrimary(c.id, c.contact_type)}>Make primary</button>}
                    <button className="drawer-close" onClick={() => removeContact(c.id)} title="Remove">×</button>
                  </div>
                ))}
              </>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 14, alignItems: 'flex-end' }}>
              <div className="form-field" style={{ width: 90 }}>
                <label>Type</label>
                <select value={newContact.contact_type} onChange={e => setNewContact(c => ({ ...c, contact_type: e.target.value }))}>
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                </select>
              </div>
              <div className="form-field" style={{ flex: 1 }}>
                <label>Value</label>
                <input value={newContact.value} onChange={e => setNewContact(c => ({ ...c, value: e.target.value }))} />
              </div>
              <div className="form-field" style={{ flex: 1 }}>
                <label>Label</label>
                <input value={newContact.label} onChange={e => setNewContact(c => ({ ...c, label: e.target.value }))} placeholder="e.g. AP Team" />
              </div>
              {isEdit && (
                <button className="btn btn-secondary" onClick={addContact}>+ Add now</button>
              )}
            </div>
            {isEdit && newContact.value.trim() && (
              <p className="form-hint" style={{ marginTop: 6 }}>
                This will also be saved automatically when you click "Save changes" below — "+ Add now" only saves it immediately, in case you want to add several before closing.
              </p>
            )}
          </div>

          <div className="form-field span-2" style={{ marginTop: 24 }}>
            <label>Notes</label>
            <textarea rows={3} value={form.notes || ''} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>

        <div className="drawer-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add client'}
          </button>
        </div>
      </div>
    </>
  );
}
